import { BookOpen, ChevronDown, Gauge, LogOut, Menu, Settings, TrendingUp, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { initials, planName } from "../lib/utils";
import type { CourseCatalog } from "../types/api";
import { Logo } from "./Logo";
import { LessonSidebar } from "./LessonSidebar";

export function DashboardLayout() {
  const { user, logout } = useAuth();
  const [catalog, setCatalog] = useState<CourseCatalog | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    api<CourseCatalog>("/api/catalog")
      .then((data) => active && setCatalog(data))
      .catch(() => active && setCatalog(null));
    return () => { active = false; };
  }, [location.pathname]);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  async function signOut() {
    await logout();
    navigate("/");
  }

  return (
    <div className="app-shell">
      <aside className={`app-sidebar ${drawerOpen ? "drawer-open" : ""}`}>
        <div className="sidebar-top">
          <Logo />
          <button className="sidebar-close" aria-label="Close course menu" onClick={() => setDrawerOpen(false)}><X /></button>
        </div>
        <nav className="sidebar-main" aria-label="Course navigation">
          <div className="sidebar-primary">
            <NavLink to="/dashboard"><Gauge size={18} />Dashboard</NavLink>
            <NavLink to="/progress"><TrendingUp size={18} />Progress</NavLink>
          </div>
          {catalog ? <LessonSidebar catalog={catalog} /> : <div className="sidebar-skeleton"><span /><span /><span /><span /></div>}
        </nav>
        <div className="sidebar-foot">
          <div className="plan-chip"><span className={`tier-icon tier-${user?.plan || "none"}`}>{user?.plan || "—"}</span><div><strong>{planName(user?.plan || null)}</strong><small>{user?.plan ? "Lifetime access" : "Choose a course"}</small></div></div>
        </div>
      </aside>
      {drawerOpen && <button className="drawer-backdrop" aria-label="Close navigation" onClick={() => setDrawerOpen(false)} />}
      <div className="app-main">
        <header className="app-topbar">
          <button className="course-menu-button" onClick={() => setDrawerOpen(true)}><Menu size={20} /><span>Course</span></button>
          <Link className="topbar-course" to="/dashboard"><BookOpen size={17} /> Roblox Studio Programming</Link>
          <div className="account-menu">
            <button className="account-trigger" onClick={() => setAccountOpen((value) => !value)} aria-expanded={accountOpen}>
              <span className="avatar">{initials(user?.displayName || user?.email || "Student")}</span>
              <span className="account-copy"><strong>{user?.displayName || "Student"}</strong><small>{planName(user?.plan || null)}</small></span>
              <ChevronDown size={16} />
            </button>
            {accountOpen && (
              <div className="account-popover">
                <Link to="/account" onClick={() => setAccountOpen(false)}><Settings size={16} />Account</Link>
                <button onClick={signOut}><LogOut size={16} />Log out</button>
              </div>
            )}
          </div>
        </header>
        <main className="app-content"><Outlet context={{ catalog, setCatalog }} /></main>
      </div>
    </div>
  );
}
